import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCases, deleteCase } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { 
  Briefcase, 
  Plus, 
  Search, 
  MoreVertical, 
  Trash2, 
  Edit,
  FileText,
  ArrowRight
} from 'lucide-react';

export default function CasesPage() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [caseToDelete, setCaseToDelete] = useState(null);

  useEffect(() => {
    loadCases();
  }, []);

  const loadCases = async () => {
    try {
      const res = await getCases();
      setCases(res.data);
    } catch (error) {
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!caseToDelete) return;
    
    try {
      await deleteCase(caseToDelete.id);
      setCases(cases.filter(c => c.id !== caseToDelete.id));
      toast.success('Case deleted successfully');
    } catch (error) {
      toast.error('Failed to delete case');
    } finally {
      setDeleteDialogOpen(false);
      setCaseToDelete(null);
    }
  };

  const filteredCases = cases.filter(c => 
    c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.client_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.case_number && c.case_number.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-6" data-testid="cases-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">Cases</h1>
            <p className="font-body text-muted-foreground mt-1">
              Manage your legal cases and documents
            </p>
          </div>
          <Link to="/cases/new">
            <Button className="bg-navy hover:bg-navy-light font-body" data-testid="new-case-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Case
            </Button>
          </Link>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search cases..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 font-body"
            data-testid="search-cases-input"
          />
        </div>

        {/* Cases Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-48 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
        ) : filteredCases.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCases.map((caseItem, index) => (
              <Card 
                key={caseItem.id} 
                className="border-border shadow-sm hover:shadow-md hover:border-bronze/30 transition-all duration-200 animate-fade-in"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`case-card-${caseItem.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 rounded-md bg-navy/10 dark:bg-navy-light/20 flex items-center justify-center">
                      <Briefcase className="w-5 h-5 text-navy dark:text-blue-400" />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`case-menu-${caseItem.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/cases/${caseItem.id}/edit`} className="flex items-center">
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-destructive focus:text-destructive"
                          onClick={() => {
                            setCaseToDelete(caseItem);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <Link to={`/cases/${caseItem.id}`}>
                    <h3 className="font-heading text-lg font-semibold text-foreground hover:text-bronze transition-colors line-clamp-1">
                      {caseItem.title}
                    </h3>
                  </Link>
                  <p className="font-body text-sm text-muted-foreground mt-1 line-clamp-2">
                    {caseItem.description || 'No description'}
                  </p>

                  <div className="flex items-center gap-2 mt-4">
                    {caseItem.case_number && (
                      <Badge variant="secondary" className="font-mono text-xs">
                        {caseItem.case_number}
                      </Badge>
                    )}
                    <Badge variant="outline" className="font-body text-xs">
                      <FileText className="w-3 h-3 mr-1" />
                      {caseItem.document_count} docs
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                    <div>
                      <p className="font-body text-xs text-muted-foreground uppercase tracking-wider">Client</p>
                      <p className="font-body text-sm font-medium text-foreground">{caseItem.client_name}</p>
                    </div>
                    <Link to={`/cases/${caseItem.id}`}>
                      <Button variant="ghost" size="sm" className="font-body text-bronze" data-testid={`view-case-${caseItem.id}`}>
                        View
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Briefcase className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-heading text-xl text-foreground mb-2">
              {searchQuery ? 'No cases found' : 'No cases yet'}
            </h3>
            <p className="font-body text-muted-foreground mb-6">
              {searchQuery 
                ? 'Try a different search term' 
                : 'Create your first case to get started'}
            </p>
            {!searchQuery && (
              <Link to="/cases/new">
                <Button className="bg-navy hover:bg-navy-light font-body" data-testid="create-first-case-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Case
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete Case?</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              This will permanently delete "{caseToDelete?.title}" and all its documents. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-body">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 font-body"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
